import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAdmin } from '../../contexts/AdminContext';
import { 
  FaArrowLeft, FaArrowRight, FaPlus, FaEdit, FaTrash, FaImage, FaBox, FaList, 
  FaSignOutAlt, FaChartBar, FaUsers, FaCog, FaEye, FaSearch, FaBell, FaMapMarkerAlt
} from 'react-icons/fa';
import * as api from '../../services/api';
import './ProductManagement.css';

function ProductManagement() {
  const { adminUser, logout } = useAdmin();
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [productsPerPage] = useState(12);
  const [formData, setFormData] = useState({
    name: '',
    category_id: '',
    image: ''
  });
  const [imageInputMode, setImageInputMode] = useState('url');
  const [imageFile, setImageFile] = useState(null);

  useEffect(() => {
    loadData();
    // Scroll to top when component mounts
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  // Live search functionality
  useEffect(() => {
    if (searchTerm.trim() === '') {
      setFilteredProducts(products);
    } else {
      const filtered = products.filter(product =>
        product.name.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredProducts(filtered);
    }
    // Reset to first page when search changes
    setCurrentPage(1);
  }, [searchTerm, products]);

  // Pagination calculations
  const indexOfLastProduct = currentPage * productsPerPage;
  const indexOfFirstProduct = indexOfLastProduct - productsPerPage;
  const currentProducts = filteredProducts.slice(indexOfFirstProduct, indexOfLastProduct);
  const totalPages = Math.ceil(filteredProducts.length / productsPerPage);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);
  const nextPage = () => {
    if (currentPage < totalPages) setCurrentPage(currentPage + 1);
  };
  const prevPage = () => {
    if (currentPage > 1) setCurrentPage(currentPage - 1);
  };

  const loadData = async () => {
    try {
      setLoading(true);
      const [productsData, categoriesData] = await Promise.all([
        api.getProducts(),
        api.getCategories()
      ]);
      
      console.log('Products data:', productsData);
      console.log('Categories data:', categoriesData);
      
      setProducts(productsData);
      setFilteredProducts(productsData);
      setCategories(categoriesData);
      setError('');
    } catch (err) {
      setError('Failed to load data. Make sure the backend server is running.');
      console.error('Error loading data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      let payload = { ...formData };

      if (imageInputMode === 'file' && imageFile) {
        const base64 = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result);
          reader.onerror = reject;
          reader.readAsDataURL(imageFile);
        });
        payload.image = base64;
      }

      if (editingProduct) {
        await api.updateProduct(editingProduct.id, payload);
      } else {
        await api.createProduct(payload);
      }
      resetForm();
      await loadData();
    } catch (err) {
      alert('Failed to save product: ' + err.message);
      console.error('Error saving product:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (product) => {
    setEditingProduct(product);
    setFormData({
      name: product.name,
      category_id: product.category_id,
      image: product.image
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this product?')) {
      try {
        setLoading(true);
        await api.deleteProduct(id);
        await loadData();
      } catch (err) {
        alert('Failed to delete product: ' + err.message);
        console.error('Error deleting product:', err);
      } finally {
        setLoading(false);
      }
    }
  };

  const resetForm = () => {
    setFormData({ name: '', category_id: '', image: '' });
    setEditingProduct(null);
    setShowModal(false);
    setImageInputMode('url');
    setImageFile(null);
  };

  const getCategoryName = (categoryId) => {
    if (!categories || categories.length === 0) {
      console.log('Categories not loaded yet or empty');
      return 'Loading...';
    }
    
    // Handle both string and number category IDs
    const category = categories.find(c => 
      c.id === categoryId || 
      c.id === parseInt(categoryId) || 
      c.id === String(categoryId)
    );
    
    console.log(`Looking for category ID ${categoryId} (type: ${typeof categoryId}) in categories:`, categories);
    console.log('Found category:', category);
    
    return category ? category.name : `Unknown (ID: ${categoryId})`;
  };

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  return (
    <div className="admin-dashboard">
      {/* Modern Sidebar */}
      <div className="admin-sidebar">
        <div className="sidebar-header">
          <div className="logo-container">
            <img src="/pharmacare-logo.png" alt="Pharmacare" className="admin-logo" />
            <div className="logo-text">
              <h2>Pharmacare</h2>
              <span>Admin Panel</span>
            </div>
          </div>
        </div>

        <nav className="sidebar-nav">
          <div className="nav-section">
            <h3>Overview</h3>
            <Link to="/admin" className="nav-item">
              <FaChartBar />
              <span>Dashboard</span>
              <FaArrowRight />
            </Link>
          </div>

          <div className="nav-section">
            <h3>Management</h3>
            <div className="nav-item active">
              <FaBox />
              <span>Products</span>
              <FaArrowRight />
            </div>
            <Link to="/admin/categories" className="nav-item">
              <FaList />
              <span>Categories</span>
              <FaArrowRight />
            </Link>
            <Link to="/admin/branches" className="nav-item">
              <FaMapMarkerAlt />
              <span>Branches</span>
              <FaArrowRight />
            </Link>
          </div>

          <div className="nav-section">
            <h3>System</h3>
            <div className="nav-item">
              <FaUsers />
              <span>Users</span>
              <span className="badge">Soon</span>
            </div>
            <div className="nav-item">
              <FaCog />
              <span>Settings</span>
              <span className="badge">Soon</span>
            </div>
          </div>
        </nav>
      </div>

      {/* Main Content */}
      <div className="admin-main">
        {/* Top Header */}
        <div className="admin-topbar">
          <div className="topbar-left">
            <h1>Product Management</h1>
            <p>Manage your product inventory</p>
          </div>
          <div className="topbar-right">
            <div className="search-box">
              <FaSearch />
              <input 
                type="text" 
                placeholder="Search products..." 
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <div className="notifications">
              <FaBell />
            </div>
            <div className="user-menu">
              <div className="user-avatar">
                {adminUser?.username?.charAt(0).toUpperCase()}
              </div>
              <div className="user-info">
                <span>{adminUser?.username}</span>
                <small>Administrator</small>
              </div>
              <button onClick={handleLogout} className="logout-btn">
                <FaSignOutAlt />
              </button>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="dashboard-content">
          <div className="page-header">
            <div className="page-header-left">
              <Link to="/admin" className="back-button">
                <FaArrowLeft /> Back to Dashboard
              </Link>
            </div>
            <div className="page-header-right">
              <button onClick={() => setShowModal(true)} className="add-product-btn">
                <FaPlus /> Add Product
              </button>
            </div>
          </div>

          <div className="content-container">
            {error && (
              <div className="error-banner">
                {error}
              </div>
            )}
            {loading && !error ? (
              <div className="loading-state">
                <div className="loading-spinner"></div>
                <p>Loading products...</p>
              </div>
            ) : (
              <div className="products-grid">
                {filteredProducts.length === 0 ? (
                  <div className="empty-state">
                    <FaBox className="empty-icon" />
                    <h3>{searchTerm ? 'No Products Found' : 'No Products Yet'}</h3>
                    <p>{searchTerm ? `No products match "${searchTerm}"` : 'Start building your inventory by adding your first product.'}</p>
                    {!searchTerm && (
                      <button onClick={() => setShowModal(true)} className="add-first-product-btn">
                        <FaPlus /> Add Your First Product
                      </button>
                    )}
                  </div>
                ) : (
                  currentProducts.map(product => (
                    <div key={product.id} className="product-card">
                      <div className="product-image">
                        {product.image_url ? (
                          <img 
                            src={product.image_url} 
                            alt={product.name}
                            loading="lazy"
                            decoding="async"
                            onError={(e) => {
                              e.target.style.display = 'none';
                              e.target.nextSibling.style.display = 'flex';
                            }}
                            style={{ maxWidth: '100%', height: 'auto' }}
                          />
                        ) : null}
                        <FaImage className="placeholder-icon" style={{ display: product.image_url ? 'none' : 'flex' }} />
                      </div>
                      <div className="product-info">
                        <h3>{product.name}</h3>
                        <p className="product-category">
                          {product.category_name || getCategoryName(product.category_id)}
                        </p>
                      </div>
                      <div className="product-actions">
                        <button onClick={() => handleEdit(product)} className="edit-btn" disabled={loading}>
                          <FaEdit />
                        </button>
                        <button onClick={() => handleDelete(product.id)} className="delete-btn" disabled={loading}>
                          <FaTrash />
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}

            {/* Pagination */}
            {!loading && filteredProducts.length > productsPerPage && (
              <div className="pagination-container">
                <div className="pagination-info">
                  Showing {indexOfFirstProduct + 1} to {Math.min(indexOfLastProduct, filteredProducts.length)} of {filteredProducts.length} products
                </div>
                <div className="pagination">
                  <button 
                    onClick={prevPage} 
                    disabled={currentPage === 1}
                    className="pagination-btn"
                  >
                    <FaArrowLeft /> Previous
                  </button>
                  
                  <div className="pagination-numbers">
                    {[...Array(totalPages)].map((_, index) => {
                      const pageNumber = index + 1;
                      // Show first page, last page, current page, and pages around current
                      if (
                        pageNumber === 1 ||
                        pageNumber === totalPages ||
                        (pageNumber >= currentPage - 1 && pageNumber <= currentPage + 1)
                      ) {
                        return (
                          <button
                            key={pageNumber}
                            onClick={() => paginate(pageNumber)}
                            className={`pagination-number ${currentPage === pageNumber ? 'active' : ''}`}
                          >
                            {pageNumber}
                          </button>
                        );
                      } else if (
                        pageNumber === currentPage - 2 ||
                        pageNumber === currentPage + 2
                      ) {
                        return <span key={pageNumber} className="pagination-ellipsis">...</span>;
                      }
                      return null;
                    })}
                  </div>

                  <button 
                    onClick={nextPage} 
                    disabled={currentPage === totalPages}
                    className="pagination-btn"
                  >
                    Next <FaArrowRight />
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {showModal && (
        <div className="modal-overlay" onClick={resetForm}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>{editingProduct ? 'Edit Product' : 'Add New Product'}</h2>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Product Name</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  placeholder="Enter product name"
                />
              </div>

              <div className="form-group">
                <label>Category</label>
                <select
                  value={formData.category_id}
                  onChange={(e) => setFormData({ ...formData, category_id: parseInt(e.target.value) })}
                  required
                >
                  <option value="">Select a category</option>
                  {categories.map(cat => (
                    <option key={cat.id} value={cat.id}>{cat.name}</option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Product Image</label>
                <div className="image-input-toggle" style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem' }}>
                  <button type="button" className={`toggle-btn ${imageInputMode === 'url' ? 'active' : ''}`} onClick={() => setImageInputMode('url')}>Use URL</button>
                  <button type="button" className={`toggle-btn ${imageInputMode === 'file' ? 'active' : ''}`} onClick={() => setImageInputMode('file')}>Upload File</button>
                </div>

                {imageInputMode === 'url' ? (
                  <>
                    <input
                      type="text"
                      value={formData.image}
                      onChange={(e) => setFormData({ ...formData, image: e.target.value })}
                      placeholder="https://example.com/image.jpg or /local-image.jpg"
                    />
                    {formData.image && (
                      <div className="image-preview">
                        <img src={formData.image} alt="Preview" />
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => setImageFile(e.target.files && e.target.files[0] ? e.target.files[0] : null)}
                    />
                    {imageFile && (
                      <div className="image-preview">
                        <img src={URL.createObjectURL(imageFile)} alt="Preview" />
                      </div>
                    )}
                  </>
                )}
              </div>

              <div className="modal-actions">
                <button type="button" onClick={resetForm} className="cancel-btn" disabled={loading}>
                  Cancel
                </button>
                <button type="submit" className="submit-btn" disabled={loading}>
                  {loading ? 'Saving...' : (editingProduct ? 'Update' : 'Add')} Product
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default ProductManagement;

